/* Karten-Migration — MapLibre GL heatmap/explorer map (Basic Dark).
 *
 * Drop-in replacement for the Leaflet map block in HeatmapPage,
 * selected via isMapLibreEnabled(). Exposes a Leaflet-compatible map
 * adapter through onMapRef so ALL page logic (bbox fetching, focus
 * deep-link, cluster zoom, Bug A-D fixes) works unchanged:
 *   getZoom / getCenter / setView([lat,lng],z) / fitBounds([[lat,lng],..])
 *   / invalidateSize
 * Buckets render as a GPU circle layer; deal pills & clusters reuse the
 * existing CSS classes. Cyan stays reserved for future accent work —
 * marker colours keep the established market-position semantics.
 */
import React, { useCallback, useMemo, useRef, useState } from "react";
import Map, { Marker, Popup, Source, Layer } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import "../../pages/heatmap-maplibre.css";
import { maptilerStyleUrl } from "../../config/mapProvider";
import { colourFor, formatValue } from "../../config/heatmap-layers";

const POSITION_PILL = {
  strong_below_market: "below",
  below_market: "below",
  near_market: "near",
  above_market: "above",
  strong_above_market: "strong-above",
  unknown: "normal",
};

function pillContent(d) {
  const pct = d?.price_vs_market_pct;
  let label = "Deal";
  let arrow = "▲";
  if (typeof pct === "number") {
    const n = Math.abs(Math.round(pct));
    if (pct > 0) {
      label = `+${n} %`;
      arrow = "▼";
    } else {
      label = `−${n} %`;
    }
  }
  // Shared deal-map contract: low-confidence deals render "normal" (⚪).
  const style = d?.low_confidence
    ? "normal"
    : POSITION_PILL[d?.market_position] || "normal";
  const lowConf = d?.low_confidence ? " deal-pill--lowconf" : "";
  return { text: `${arrow} ${label}`, className: `deal-pill deal-pill--${style}${lowConf}` };
}

function clusterSize(count) {
  return count >= 500 ? 48 : count >= 100 ? 42 : 36;
}
function clusterLabel(count) {
  return count >= 1000 ? `${Math.round(count / 100) / 10}k` : count;
}

export default function HeatmapMapLibre({
  initial,
  onMapRef,
  onBoundsChange,
  buckets,
  domain,
  layer,
  markers,
  clusters,
  clustered,
  showMarket = true,
  showDeals,
  focus,
  onOpenListing,
  t,
}) {
  const adapterRef = useRef(null);
  const [bucketPopup, setBucketPopup] = useState(null);
  const [dealPopup, setDealPopup] = useState(null);
  const [focusPopup, setFocusPopup] = useState(false);
  const [cursor, setCursor] = useState("grab");

  const handleRef = useCallback(
    (instance) => {
      if (!instance) {
        adapterRef.current = null;
        onMapRef(null);
        return;
      }
      const map = instance.getMap ? instance.getMap() : instance;
      const adapter = {
        getZoom: () => map.getZoom(),
        getCenter: () => map.getCenter(),
        setView: ([lat, lng], zoom) => map.jumpTo({ center: [lng, lat], zoom }),
        fitBounds: (llBounds, opts = {}) => {
          const [[aLat, aLng], [bLat, bLng]] = llBounds;
          const padding = Array.isArray(opts.padding)
            ? opts.padding[0]
            : opts.padding ?? 0;
          map.fitBounds(
            [
              [Math.min(aLng, bLng), Math.min(aLat, bLat)],
              [Math.max(aLng, bLng), Math.max(aLat, bLat)],
            ],
            { padding },
          );
        },
        invalidateSize: () => map.resize(),
      };
      adapterRef.current = adapter;
      onMapRef(adapter);
    },
    [onMapRef],
  );

  const bucketsGeojson = useMemo(
    () => ({
      type: "FeatureCollection",
      features: (buckets || [])
        .filter(
          (b) =>
            Number.isFinite(b?.latitude) && Number.isFinite(b?.longitude),
        )
        .map((b) => ({
          type: "Feature",
          geometry: { type: "Point", coordinates: [b.longitude, b.latitude] },
          properties: {
            fill: colourFor(b.avg, domain[0], domain[1], layer?.colorScale),
            r: Math.max(7, Math.min(20, Math.sqrt(b.n_samples || 1) * 2)),
            bucket_key: b.bucket_key,
            bucket_type: b.bucket_type,
            avg: b.avg,
            median: b.median,
            p25: b.p25,
            p75: b.p75,
            n_samples: b.n_samples,
          },
        })),
    }),
    [buckets, domain, layer],
  );

  const handleMapClick = useCallback((evt) => {
    const feature = evt.features?.[0];
    if (!feature) {
      setBucketPopup(null);
      return;
    }
    const [lng, lat] = feature.geometry.coordinates;
    setDealPopup(null);
    setBucketPopup({ lng, lat, p: feature.properties });
  }, []);

  const zoomToCluster = useCallback((c) => {
    const m = adapterRef.current;
    if (!m) return;
    const [aLat, aLng, bLat, bLng] = c.bounds || [];
    if (
      Number.isFinite(aLat) &&
      Number.isFinite(bLat) &&
      (Math.abs(bLat - aLat) > 0.0005 || Math.abs(bLng - aLng) > 0.0005)
    ) {
      m.fitBounds(
        [
          [aLat, aLng],
          [bLat, bLng],
        ],
        { padding: [48, 48] },
      );
    } else {
      m.setView([c.lat, c.lng], Math.min(18, (m.getZoom() ?? 10) + 2));
    }
  }, []);

  return (
    <Map
      ref={handleRef}
      data-testid="heatmap-maplibre"
      initialViewState={{
        longitude: initial.center[1],
        latitude: initial.center[0],
        zoom: initial.zoom,
      }}
      mapStyle={maptilerStyleUrl()}
      style={{ height: "100%", width: "100%", background: "#09090b" }}
      attributionControl={{ compact: true }}
      interactiveLayerIds={["bucket-circles"]}
      cursor={cursor}
      onMouseEnter={() => setCursor("pointer")}
      onMouseLeave={() => setCursor("grab")}
      onClick={handleMapClick}
      onLoad={(evt) => onBoundsChange(evt.target.getBounds())}
      onMoveEnd={(evt) => onBoundsChange(evt.target.getBounds())}
    >
      {/* Heatmap buckets — GPU circle layer (same colours/radii as Leaflet).
          Rendered via createElement (not JSX) so the dev-mode visual-edits
          babel plugin can't inject x-* attrs into the maplibre source spec.
          Independently toggleable from the deals layer (shared contract). */}
      {showMarket && React.createElement(
        Source,
        { key: "buckets", id: "buckets", type: "geojson", data: bucketsGeojson },
        React.createElement(Layer, {
          id: "bucket-circles",
          type: "circle",
          paint: {
            "circle-color": ["get", "fill"],
            "circle-radius": ["get", "r"],
            "circle-opacity": 0.78,
            "circle-stroke-color": "#18181b",
            "circle-stroke-width": 1,
          },
        }),
      )}

      {/* Deal pills — clickable entry point into DealAlert. */}
      {showDeals &&
        !clustered &&
        (markers || []).map((d) => {
          if (!Number.isFinite(d.lat) || !Number.isFinite(d.lng)) return null;
          const pill = pillContent(d);
          return (
            <Marker
              key={`deal-${d.ulid}`}
              longitude={d.lng}
              latitude={d.lat}
              anchor="center"
              style={{ zIndex: 3 }}
              onClick={(e) => {
                e.originalEvent.stopPropagation();
                setBucketPopup(null);
                setDealPopup(d);
              }}
            >
              <span className="heatmap-deal-marker ml-marker">
                <span
                  data-testid={`heatmap-deal-pin-${d.ulid}`}
                  className={pill.className}
                >
                  {pill.text}
                </span>
              </span>
            </Marker>
          );
        })}

      {/* Zoom-dependent server clusters (click → zoom in). */}
      {showDeals &&
        clustered &&
        (clusters || []).map((c, i) => {
          const size = clusterSize(c.count);
          return (
            <Marker
              key={`cluster-${i}-${c.count}`}
              longitude={c.lng}
              latitude={c.lat}
              anchor="center"
              style={{ zIndex: 2 }}
              onClick={(e) => {
                e.originalEvent.stopPropagation();
                zoomToCluster(c);
              }}
            >
              <span className="heatmap-cluster-marker">
                <span
                  className="cluster-bubble"
                  style={{
                    width: size,
                    height: size,
                    lineHeight: `${size}px`,
                  }}
                >
                  {clusterLabel(c.count)}
                </span>
              </span>
            </Marker>
          );
        })}

      {/* Deep-link focus marker (listing detail → heatmap). */}
      {focus && (
        <Marker
          longitude={focus.lng}
          latitude={focus.lat}
          anchor="center"
          style={{ zIndex: 4 }}
          onClick={(e) => {
            e.originalEvent.stopPropagation();
            setFocusPopup(true);
          }}
        >
          <span className="ml-focus-ring heatmap-focus-marker" />
        </Marker>
      )}

      {/* Popups */}
      {bucketPopup && (
        <Popup
          key={`bp-${bucketPopup.p.bucket_key}`}
          longitude={bucketPopup.lng}
          latitude={bucketPopup.lat}
          // No fixed anchor — MapLibre picks the side dynamically so the
          // popup ALWAYS stays inside the visible map area (P0-Audit).
          maxWidth="260px"
          offset={10}
          className="heatmap-ml-popup"
          onClose={() => setBucketPopup(null)}
        >
          <div
            data-testid={`heatmap-popup-${bucketPopup.p.bucket_key}`}
            className="min-w-[180px] space-y-1 text-zinc-100"
          >
            <div className="font-semibold text-amber-300">
              {layer
                ? t(`layers.${layer.id}.label`, { defaultValue: layer.label })
                : ""}
            </div>
            <div className="text-xs text-zinc-400">
              {bucketPopup.p.bucket_type} · {bucketPopup.p.bucket_key}
            </div>
            <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
              <span className="text-zinc-400">avg</span>
              <span className="text-zinc-100 font-medium">
                {formatValue(bucketPopup.p.avg, layer?.format)}
              </span>
              {bucketPopup.p.median !== undefined && (
                <>
                  <span className="text-zinc-400">median</span>
                  <span className="text-zinc-100">
                    {formatValue(bucketPopup.p.median, layer?.format)}
                  </span>
                </>
              )}
              {bucketPopup.p.p25 !== undefined && (
                <>
                  <span className="text-zinc-400">p25</span>
                  <span className="text-zinc-100">
                    {formatValue(bucketPopup.p.p25, layer?.format)}
                  </span>
                </>
              )}
              {bucketPopup.p.p75 !== undefined && (
                <>
                  <span className="text-zinc-400">p75</span>
                  <span className="text-zinc-100">
                    {formatValue(bucketPopup.p.p75, layer?.format)}
                  </span>
                </>
              )}
              <span className="text-zinc-400">samples</span>
              <span className="text-zinc-100">{bucketPopup.p.n_samples}</span>
            </div>
          </div>
        </Popup>
      )}

      {dealPopup && (
        <Popup
          key={`dp-${dealPopup.ulid}`}
          longitude={dealPopup.lng}
          latitude={dealPopup.lat}
          // Viewport-aware auto-anchor: near the top edge the popup opens
          // below the marker, near the sides it opens left/right, etc.
          maxWidth="240px"
          className="heatmap-ml-popup heatmap-ml-deal-popup"
          offset={14}
          onClose={() => setDealPopup(null)}
        >
          <div
            data-testid={`heatmap-deal-popup-${dealPopup.ulid}`}
            className="w-[230px] overflow-hidden text-zinc-100"
          >
            {dealPopup.primary_image_url && (
              <img
                src={dealPopup.primary_image_url}
                alt=""
                loading="lazy"
                className="h-28 w-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            )}
            <div className="space-y-1.5 p-3">
              <div className="line-clamp-2 text-[13px] font-medium leading-snug text-zinc-100">
                {dealPopup.title}
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-zinc-100">
                  {typeof dealPopup.price === "number"
                    ? `€ ${Math.round(dealPopup.price).toLocaleString("de-AT")}`
                    : "—"}
                </span>
                {typeof dealPopup.price_vs_market_pct === "number" && (
                  <span
                    className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                      dealPopup.price_vs_market_pct > 0
                        ? "border-orange-500/50 bg-orange-500/15 text-orange-300"
                        : "border-emerald-500/50 bg-emerald-500/15 text-emerald-300"
                    }`}
                  >
                    {dealPopup.price_vs_market_pct > 0 ? "+" : "−"}
                    {Math.abs(Math.round(dealPopup.price_vs_market_pct))} %
                  </span>
                )}
              </div>
              {dealPopup.low_confidence && (
                <div
                  data-testid={`heatmap-deal-lowconf-${dealPopup.ulid}`}
                  className="text-[10px] font-medium text-amber-400/90"
                >
                  {t("lowConfidence")}
                </div>
              )}
              <button
                type="button"
                data-testid={`heatmap-deal-open-${dealPopup.ulid}`}
                onClick={() => onOpenListing(dealPopup.ulid)}
                className="mt-1 w-full rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-zinc-950 transition-colors hover:bg-amber-400"
              >
                {t("viewDeal")}
              </button>
            </div>
          </div>
        </Popup>
      )}

      {focus && focusPopup && (
        <Popup
          longitude={focus.lng}
          latitude={focus.lat}
          offset={14}
          className="heatmap-ml-popup"
          onClose={() => setFocusPopup(false)}
        >
          <div className="min-w-[140px] text-zinc-100">
            <div className="font-semibold text-amber-300">{t("yourDeal")}</div>
            <div className="text-xs text-zinc-400">{t("approxLocation")}</div>
          </div>
        </Popup>
      )}
    </Map>
  );
}
